import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, LogOut, ArrowLeft, Camera, ScanLine, Bus, Check, X, Users, ChevronUp } from "lucide-react";
import { api, formatDate, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WEEKDAY = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long" }); }
  catch { return ""; }
};
const paxOf = (b) => (b.adults || 0) + (b.children || 0) || 1;
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ScanCheckin() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const scannerRef = useRef(null);
  const modalRef = useRef(false);
  const busyRef = useRef(false);
  const touchStartY = useRef(null);
  const [tours, setTours] = useState([]);
  const [cancelledNames, setCancelledNames] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busDate, setBusDate] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scanModal, setScanModal] = useState(null); // {status, name, booking, message}
  const [camError, setCamError] = useState("");
  const [toggling, setToggling] = useState({}); // booking_id -> bool

  const loadRoster = useCallback(async () => {
    try {
      const r = await api.get("/admin/bookings");
      const cancelled = new Set(r.data.cancelled_tour_names || []);
      const paid = (r.data.tour_bookings || []).filter(
        (b) => b.payment_status === "paid" && !cancelled.has(b.tour_name)
      );
      setTours(paid);
      setCancelledNames(cancelled);
      return paid;
    } catch (_) {
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoster().then((paid) => {
      const dates = Array.from(new Set(paid.map((b) => b.date))).sort();
      const t = todayISO();
      setBusDate(dates.includes(t) ? t : dates[0] || "");
    });
  }, [loadRoster]);

  const dates = Array.from(new Set(tours.map((b) => b.date))).sort();
  const roster = tours
    .filter((b) => b.date === busDate)
    .sort((a, b) => (a.customer?.full_name || "").localeCompare(b.customer?.full_name || ""));
  const checkedCount = roster.filter((b) => b.checked_in).length;
  const totalPax = roster.reduce((s, b) => s + paxOf(b), 0);
  const checkedPax = roster.filter((b) => b.checked_in).reduce((s, b) => s + paxOf(b), 0);
  const noShowCount = roster.filter((b) => b.no_show).length;

  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { await s.stop(); } catch (_) {}
      try { await s.clear(); } catch (_) {}
    }
  }, []);

  const audioRef = useRef(null);
  const ensureAudio = () => {
    try {
      if (!audioRef.current) { const AC = window.AudioContext || window.webkitAudioContext; if (AC) audioRef.current = new AC(); }
      if (audioRef.current?.state === "suspended") audioRef.current.resume();
    } catch (_) {}
  };
  const feedback = (success) => {
    try { navigator.vibrate?.(success ? 80 : [50, 60, 50]); } catch (_) {}
    try {
      const ctx = audioRef.current; if (!ctx) return;
      const now = ctx.currentTime;
      const tones = success ? [880, 1320] : [300];
      tones.forEach((freq, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = freq; o.connect(g); g.connect(ctx.destination);
        const start = now + i * 0.12; const dur = success ? 0.11 : 0.35;
        g.gain.setValueAtTime(0.0001, start);
        g.gain.exponentialRampToValueAtTime(0.25, start + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
        o.start(start); o.stop(start + dur + 0.02);
      });
    } catch (_) {}
  };

  const showResult = (r) => {
    modalRef.current = true;
    setScanModal(r);
    feedback(r.status === "ok" || r.status === "already");
    try { scannerRef.current?.pause(true); } catch (_) {}
  };

  const dismissResult = useCallback(() => {
    modalRef.current = false;
    setScanModal(null);
    try { scannerRef.current?.resume(); } catch (_) {}
  }, []);

  // Scan handler: only accept guests whose paid tour is on the selected bus date.
  const processScan = useCallback(async (rawId) => {
    const m = String(rawId).match(/checkin\/([0-9a-fA-F-]{6,})/);
    const id = m ? m[1] : String(rawId).trim();
    busyRef.current = true;
    setBusy(true);
    try {
      const all = await api.get("/admin/bookings");
      const byId = {};
      (all.data.tour_bookings || []).forEach((b) => (byId[b.id] = b));
      (all.data.dinner_reservations || []).forEach((b) => (byId[b.id] = b));
      const b = byId[id];
      if (!b) {
        showResult({ status: "error", message: "Booking not found or invalid QR code." });
      } else if (b.type === "dinner") {
        showResult({ status: "error", name: b.customer?.full_name, booking: b, message: "This is a dinner reservation — not a bus transfer." });
      } else if (b.payment_status !== "paid") {
        showResult({ status: "error", name: b.customer?.full_name, booking: b, message: "This booking is not paid/confirmed." });
      } else if (b.date !== busDate) {
        showResult({ status: "wrong-day", name: b.customer?.full_name, booking: b, message: `Booked for ${WEEKDAY(b.date)} ${formatDate(b.date)} — NOT today's bus.` });
      } else {
        const r = await api.post(`/staff/checkin/${id}`);
        showResult({ status: r.data.already_checked_in ? "already" : "ok", name: b.customer?.full_name, booking: b });
        await loadRoster();
      }
    } catch (e) {
      showResult({ status: "error", message: formatApiErrorDetail(e.response?.data?.detail) || "Check-in failed." });
    }
    busyRef.current = false;
    setBusy(false);
  }, [busDate, loadRoster]);

  const startScanner = useCallback(async () => {
    setCamError("");
    await stopScanner();
    try {
      const h5 = new Html5Qrcode("qr-reader", { verbose: false });
      scannerRef.current = h5;
      await h5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decodedText) => { if (modalRef.current || busyRef.current) return; await processScan(decodedText); },
        () => {}
      );
    } catch (e) {
      setCamError("Unable to access the camera. Allow camera permission or tick guests off the list below.");
    }
  }, [stopScanner, processScan]);

  useEffect(() => {
    if (scanning) startScanner(); else stopScanner();
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning, busDate]);

  const toggleCheck = async (b) => {
    const next = !b.checked_in;
    setToggling((t) => ({ ...t, [b.id]: true }));
    setTours((list) => list.map((x) => (x.id === b.id ? { ...x, checked_in: next, no_show: next ? false : x.no_show } : x)));
    try {
      await api.post(`/admin/bookings/${b.id}/checkin`, { checked_in: next });
    } catch (_) {
      setTours((list) => list.map((x) => (x.id === b.id ? { ...x, checked_in: !next } : x)));
    } finally {
      setToggling((t) => { const n = { ...t }; delete n[b.id]; return n; });
    }
  };

  const markNoShow = async (b) => {
    const next = !b.no_show;
    setToggling((t) => ({ ...t, [b.id]: true }));
    setTours((list) => list.map((x) => (x.id === b.id ? { ...x, no_show: next, checked_in: next ? false : x.checked_in } : x)));
    try {
      await api.post(`/admin/bookings/${b.id}/no-show`, { no_show: next });
    } catch (_) {
      setTours((list) => list.map((x) => (x.id === b.id ? { ...x, no_show: !next } : x)));
    } finally {
      setToggling((t) => { const n = { ...t }; delete n[b.id]; return n; });
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div data-testid="scan-checkin-page" className="min-h-screen bg-background grain">
      <header className="border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/admin" data-testid="scan-back" className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14} /> Dashboard</Link>
          <span className="font-display font-extrabold text-lg uppercase tracking-tighter inline-flex items-center gap-2"><Bus size={18} /> Bus Check-in</span>
          <button data-testid="scan-logout" onClick={doLogout} className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2"><LogOut size={14} /> Logout</button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-6 py-8">
        {/* Bus day selector */}
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Bus departing</p>
          <div className="flex gap-2 flex-wrap" data-testid="bus-date-tabs">
            {dates.map((d) => (
              <button
                key={d}
                data-testid={`bus-date-${d}`}
                onClick={() => { setBusDate(d); dismissResult(); }}
                className={`px-4 py-2.5 text-xs uppercase tracking-[0.12em] border transition-colors ${
                  busDate === d ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/40"}`}
              >
                {WEEKDAY(d)} {formatDate(d)}
              </button>
            ))}
            {!loading && dates.length === 0 && <p className="text-sm text-gray-500">No paid tours yet.</p>}
          </div>
        </div>

        {busDate && (
          <>
            {/* Progress + scan toggle */}
            <div className="bg-[#0C0C0E] border border-white/10 p-5 mb-5 flex items-center justify-between gap-4">
              <div>
                <p data-testid="bus-progress" className="font-display font-extrabold text-2xl">{checkedCount}<span className="text-gray-600 text-lg">/{roster.length}</span> <span className="text-sm text-gray-500 font-normal">boarded</span></p>
                <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mt-1">{checkedPax} of {totalPax} pax{noShowCount > 0 ? <span data-testid="bus-no-show-count" className="text-[#C44536]"> · {noShowCount} no-show{noShowCount !== 1 ? "s" : ""}</span> : null}</p>
              </div>
              <button
                data-testid="toggle-scanner-btn"
                onClick={() => { ensureAudio(); setScanning((s) => !s); }}
                className={`uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2 ${
                  scanning ? "border border-white/30 text-white hover:bg-white/10" : "bg-white text-black hover:bg-gray-200"}`}
              >
                <ScanLine size={15} /> {scanning ? "Stop" : "Scan QR"}
              </button>
            </div>

            {/* Camera */}
            {scanning && (
              <div className="mb-6">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3 text-center">Point at the guest's QR code{busy ? " · checking…" : ""}</p>
                <div id="qr-reader" data-testid="qr-reader" className="w-full overflow-hidden border border-white/15 bg-black rounded-sm" style={{ minHeight: 280 }} />
                {camError && (
                  <div className="mt-4 p-4 border border-[#C44536]/40 bg-[#2a1210]">
                    <div className="flex items-start gap-3"><Camera size={18} className="text-[#C44536] mt-0.5" /><p className="text-sm text-gray-300">{camError}</p></div>
                    <button data-testid="retry-camera-btn" onClick={startScanner} className="mt-3 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-2.5 px-5 transition-colors inline-flex items-center gap-2"><Camera size={14} /> Retry Camera</button>
                  </div>
                )}
              </div>
            )}

            {/* Roster checklist */}
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Passenger list · tick off each QR</p>
            <div className="border border-white/10 divide-y divide-white/5" data-testid="bus-roster">
              {roster.map((b) => (
                <div
                  key={b.id}
                  data-testid={`roster-row-${b.id}`}
                  className={`flex items-center gap-3 px-4 py-3.5 transition-colors ${b.no_show ? "bg-[#2a1210]" : b.checked_in ? "bg-emerald-950/20" : "hover:bg-white/[0.03]"}`}
                >
                  <button
                    type="button"
                    data-testid={`roster-check-${b.id}`}
                    onClick={() => toggleCheck(b)}
                    disabled={!!toggling[b.id]}
                    className={`shrink-0 w-7 h-7 border flex items-center justify-center transition-colors ${
                      b.checked_in ? "bg-emerald-500 border-emerald-500 text-black" : "border-white/30 text-transparent hover:border-white/60"}`}
                  >
                    {toggling[b.id] ? <Loader2 size={14} className="animate-spin text-white" /> : <Check size={16} strokeWidth={3} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${b.no_show ? "text-[#e07a6c]" : b.checked_in ? "text-emerald-300 line-through" : "text-white"}`}>{b.customer?.full_name || "—"}</p>
                    <p className="text-xs text-gray-500 truncate">{b.tour_name}{b.slot ? ` · ${b.slot}` : ""} · {paxOf(b)} pax</p>
                  </div>
                  {b.checked_in && <span className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-emerald-400">Boarded</span>}
                  {b.no_show && <span className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-[#C44536]">No-show</span>}
                  <button
                    type="button"
                    data-testid={`roster-no-show-${b.id}`}
                    onClick={() => markNoShow(b)}
                    disabled={!!toggling[b.id]}
                    title={b.no_show ? "Undo no-show" : "Mark as no-show"}
                    className={`shrink-0 w-7 h-7 border flex items-center justify-center transition-colors ${
                      b.no_show ? "border-white/30 text-gray-400 hover:border-white/60 hover:text-white" : "border-white/15 text-gray-600 hover:border-[#C44536]/60 hover:text-[#C44536]"}`}
                  >
                    <XCircle size={15} />
                  </button>
                </div>
              ))}
              {roster.length === 0 && <p className="px-4 py-6 text-sm text-gray-500 text-center">No paid tours on this day.</p>}
            </div>
          </>
        )}
      </div>

      {scanModal && (() => {
        const s = scanModal.status;
        const b = scanModal.booking;
        const ok = s === "ok";
        const accent = ok ? "#34d399" : s === "already" ? "#eab308" : s === "wrong-day" ? "#f59e0b" : "#C44536";
        const title = ok ? "On the bus" : s === "already" ? "Already boarded" : s === "wrong-day" ? "Wrong day" : "Not valid";
        return (
          <div
            data-testid="scan-result-modal"
            onClick={dismissResult}
            onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
            onTouchEnd={(e) => { if (touchStartY.current != null && touchStartY.current - e.changedTouches[0].clientY > 55) dismissResult(); touchStartY.current = null; }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center px-6 bg-black/90 backdrop-blur-sm animate-in fade-in duration-150"
          >
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-[#0C0C0E] border rounded-sm p-8 text-center" style={{ borderColor: `${accent}66` }}>
              <div className="w-20 h-20 rounded-full border-2 flex items-center justify-center mx-auto mb-6" style={{ borderColor: accent }}>
                {ok && <CheckCircle2 size={40} style={{ color: accent }} />}
                {s === "already" && <AlertTriangle size={40} style={{ color: accent }} />}
                {s === "wrong-day" && <AlertTriangle size={40} style={{ color: accent }} />}
                {s === "error" && <XCircle size={40} style={{ color: accent }} />}
              </div>
              <p data-testid="scan-result-status" className="font-display font-extrabold uppercase tracking-tight text-2xl" style={{ color: accent }}>{title}</p>
              {scanModal.name && <p data-testid="scan-result-name" className="text-white text-xl mt-3 font-medium">{scanModal.name}</p>}
              {b && (
                <div className="mt-6 border-t border-white/10 pt-6 space-y-3 text-left">
                  <div className="flex items-center justify-center gap-2 text-center">
                    <Users size={22} style={{ color: accent }} />
                    <span data-testid="scan-result-pax" className="font-display font-extrabold text-3xl text-white">{paxOf(b)}</span>
                    <span className="text-gray-400 text-sm uppercase tracking-[0.15em]">{paxOf(b) === 1 ? "person" : "people"}</span>
                  </div>
                  {(b.adults || b.children) ? <p className="text-xs text-gray-500 text-center -mt-2">{b.adults || 0} adult{(b.adults || 0) !== 1 ? "s" : ""}{b.children ? ` · ${b.children} child${b.children !== 1 ? "ren" : ""}` : ""}</p> : null}
                  <div className="flex justify-between text-sm pt-2"><span className="text-gray-500 uppercase tracking-[0.1em] text-xs">Tour</span><span className="text-white text-right">{b.tour_name}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500 uppercase tracking-[0.1em] text-xs">Date</span><span className="text-white text-right">{WEEKDAY(b.date)} {formatDate(b.date)}{b.slot ? ` · ${b.slot}` : ""}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500 uppercase tracking-[0.1em] text-xs">Ref</span><span className="text-white text-right">{String(b.id).slice(0, 8).toUpperCase()}</span></div>
                </div>
              )}
              {scanModal.message && <p className="text-sm text-gray-400 mt-5">{scanModal.message}</p>}
              <button data-testid="scan-result-done" onClick={dismissResult} className="mt-8 w-full bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-2">
                <X size={15} /> Done — scan next
              </button>
            </div>
            <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mt-6 inline-flex items-center gap-2 animate-pulse"><ChevronUp size={14} /> Swipe up to keep scanning</p>
          </div>
        );
      })()}
    </div>
  );
}
